/**
 * Front desk (WP1-FRONTDESK): checkout, Close the Day, staff tasks with the
 * contact log, and patient documents. Routes live under `/frontdesk`.
 */
import { defineManifest } from '@velora/contracts';
import { FRONTDESK_PERMISSIONS as P } from '@velora/contracts/frontdesk';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'frontdesk',
    title: 'Front desk',
    version: '0.1.0',
    owner: 'WP1-FRONTDESK',
    entitlementKey: null,
    permissions: [...new Set(Object.values(P))],
    routes: [
      {
        id: 'checkout',
        path: '/frontdesk',
        title: 'Checkout',
        officeScope: 'location-or-all',
        permission: P.checkout,
        readsPhi: true,
      },
      {
        id: 'close',
        path: '/frontdesk/close',
        title: 'Close the day',
        officeScope: 'location-or-all',
        permission: P.moneyView,
        readsPhi: true,
      },
      {
        id: 'tasks',
        path: '/frontdesk/tasks',
        title: 'Tasks',
        officeScope: 'location-or-all',
        permission: P.tasksView,
        readsPhi: true,
      },
      {
        // NEW-WF-12 (DO-03): scans waiting to be filed.
        id: 'scanInbox',
        path: '/frontdesk/scan-inbox',
        title: 'Scan inbox',
        officeScope: 'location',
        permission: P.documentsEdit,
        readsPhi: true,
      },
      {
        id: 'patient',
        path: '/frontdesk/patient',
        title: 'Documents and contacts',
        officeScope: 'location',
        permission: P.documentsView,
        readsPhi: true,
      },
    ],
    nav: [
      {
        label: 'Checkout',
        labelKey: 'checkout',
        group: 'schedule',
        routeId: 'checkout',
        icon: 'receipt',
        order: 30,
        permission: P.checkout,
      },
      {
        label: 'Documents',
        group: 'patients',
        routeId: 'patient',
        icon: 'folder-open',
        order: 40,
        permission: P.documentsView,
      },
      {
        label: 'Scan inbox',
        group: 'patients',
        routeId: 'scanInbox',
        icon: 'inbox',
        order: 41,
        permission: P.documentsEdit,
      },
      {
        label: 'Close the day',
        group: 'revenue',
        routeId: 'close',
        icon: 'lock',
        order: 60,
        permission: P.moneyView,
      },
      {
        label: 'Tasks',
        group: 'operations',
        routeId: 'tasks',
        icon: 'list-checks',
        order: 10,
        permission: P.tasksView,
      },
    ],
    commands: [
      {
        id: 'frontdesk.checkout',
        title: 'Checkout',
        keywords: ['walkout', 'check out', 'collect', 'copay'],
        routeId: 'checkout',
        permission: P.checkout,
      },
      {
        id: 'frontdesk.close',
        title: 'Close day',
        keywords: ['end of day', 'day sheet', 'deposit', 'drawer', 'eod'],
        routeId: 'close',
        permission: P.moneyView,
      },
      {
        id: 'frontdesk.tasks',
        title: 'My tasks',
        keywords: ['to do', 'follow up', 'work'],
        routeId: 'tasks',
        permission: P.tasksView,
      },
      {
        id: 'frontdesk.documents',
        title: 'Patient documents',
        keywords: ['scan', 'upload', 'file', 'contact log', 'commlog'],
        routeId: 'patient',
        permission: P.documentsView,
      },
    ],
    shortcuts: [
      { keys: 'g o', description: 'Go to Checkout', routeId: 'checkout' },
      { keys: 'g w', description: 'Go to Tasks', routeId: 'tasks' },
    ],
    sseEntityKeys: [
      'appointment',
      'procedure',
      'payment',
      'document',
      'task',
      'interaction',
      'day_close',
      'document_inbox',
    ],
    phiReadPoints: [
      'checkout.sheet',
      'checkout.queue',
      'close.day',
      'close.daysheet',
      'contacts.patient',
      'tasks.patient',
      'documents.list',
      'documents.content',
      'documents.inbox',
    ],
  }),
  pages: {
    checkout: () => import('./pages/CheckoutPage.js'),
    close: () => import('./pages/ClosePage.js'),
    tasks: () => import('./pages/TasksPage.js'),
    patient: () => import('./pages/PatientDeskPage.js'),
    scanInbox: () => import('./pages/ScanInboxPage.js'),
  },
});
